import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);
const IDLE_MS = 8 * 60 * 60 * 1000;

export function AppProvider({ children }) {
  const [authUser, setAuthUser] = useState(null); // null = checking, false = signed out
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [biz, setBiz] = useState(localStorage.getItem("roxy_biz"));
  const [meta, setMeta] = useState({ groups: [], voucher_types: [], txn_types: [], banks: [], roles: [] });
  const [ledgers, setLedgers] = useState([]);
  const [parties, setParties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem("roxy_theme") || "dark");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("roxy_theme", theme);
  }, [theme]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setAuthUser(r.data)).catch(() => setAuthUser(false));
    const h = () => { setAuthUser(false); setReady(false); };
    window.addEventListener("roxy:unauth", h);
    return () => window.removeEventListener("roxy:unauth", h);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuthUser(false);
    setReady(false);
  }, []);

  useEffect(() => {
    if (!authUser) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => { if (Date.now() - last > IDLE_MS) logout(); }, 60000);
    return () => { evs.forEach((e) => window.removeEventListener(e, bump)); clearInterval(t); };
  }, [authUser, logout]);

  useEffect(() => {
    if (!authUser) return;
    (async () => {
      try {
        const [b, u, m] = await Promise.all([api.get("/businesses"), api.get("/users"), api.get("/meta")]);
        setBusinesses(b.data);
        setUsers(u.data);
        setMeta(m.data);
        const bid = b.data.find((x) => x.id === biz)?.id || b.data[0]?.id;
        if (bid) localStorage.setItem("roxy_biz", bid);
        setBiz(bid);
      } catch {
        /* 401 handled by interceptor */
      }
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  const refreshMasters = useCallback(async () => {
    try {
      const [l, p, a] = await Promise.all([api.get("/ledgers"), api.get("/parties"), api.get("/accounts")]);
      setLedgers(l.data);
      setParties(p.data);
      setAccounts(a.data);
    } catch {
      /* handled by page-level error states */
    }
  }, []);

  const refreshOrg = useCallback(async () => {
    const [b, u] = await Promise.all([api.get("/businesses"), api.get("/users")]);
    setBusinesses(b.data);
    setUsers(u.data);
  }, []);

  useEffect(() => {
    if (ready && biz && !authUser?.must_change_password) {
      refreshMasters();
    }
  }, [ready, biz, refreshMasters, authUser?.must_change_password]);

  const chooseBiz = (id) => { localStorage.setItem("roxy_biz", id); setBiz(id); };
  const user = authUser || null;
  const can = (...roles) => !!user && roles.includes(user.role);

  return (
    <Ctx.Provider value={{ authUser, setAuthUser, logout, businesses, users, biz, user, meta, ledgers, parties, accounts, theme, setTheme, ready,
      chooseBiz, refreshMasters, refreshOrg, can }}>
      {children}
    </Ctx.Provider>
  );
}
